import { useEffect, useState, useCallback } from "react";
import { FileText, Image as ImageIcon, Trash2, Upload, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export const RecordFiles = ({ recordId, admin = false, onCountChange }) => {
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/service-records/${recordId}/files`);
      const withLinks = await Promise.all(
        data.map(async (f) => {
          try {
            const { data: link } = await api.get(`/files/${f.id}/link`);
            return { ...f, url: `${process.env.REACT_APP_BACKEND_URL}${link.url}` };
          } catch {
            return { ...f, url: null };
          }
        })
      );
      setFiles(withLinks);
      onCountChange?.(data.length);
    } catch {
      setFiles([]);
    }
  }, [recordId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/service-records/${recordId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/admin/files/${f.id}`);
      toast.success("Attachment removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="mt-4" data-testid={`record-files-${recordId}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <Paperclip size={12} /> Attachments {files ? `(${files.length})` : ""}
        </span>
        {admin && (
          <label className="flex cursor-pointer items-center gap-2 border border-amber-600/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-amber-500 transition-colors hover:bg-amber-600 hover:text-black" data-testid={`file-upload-label-${recordId}`}>
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {uploading ? "Uploading…" : "Add photo / PDF"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} disabled={uploading} data-testid={`file-upload-input-${recordId}`} />
          </label>
        )}
      </div>

      {files && files.length === 0 && (
        <p className="mt-3 font-mono text-[11px] text-slate-600" data-testid={`record-files-empty-${recordId}`}>No photos or documents attached.</p>
      )}
      {files && files.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {files.map((f) => (
            <div key={f.id} className="group relative border border-white/10 bg-[#0D1017]" data-testid={`record-file-${f.id}`}>
              <a href={f.url || "#"} target="_blank" rel="noreferrer" className="block" data-testid={`record-file-link-${f.id}`}>
                {f.content_type.startsWith("image/") ? (
                  f.url ? <img src={f.url} alt={f.original_filename} className="aspect-square w-full object-cover" /> : <div className="flex aspect-square w-full items-center justify-center text-slate-600"><ImageIcon size={28} /></div>
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center text-slate-500"><FileText size={28} /></div>
                )}
                <div className="flex items-center gap-1.5 truncate px-2 py-1.5 font-mono text-[10px] text-slate-400">
                  {f.content_type.startsWith("image/") ? <ImageIcon size={10} /> : <FileText size={10} />}
                  <span className="truncate">{f.original_filename}</span>
                </div>
              </a>
              {admin && (
                <button onClick={() => remove(f)} className="absolute right-1 top-1 bg-black/70 p-1.5 text-slate-400 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100" data-testid={`record-file-delete-${f.id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
